import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API, formatDateBR } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Loader2, Search, RefreshCw, Edit, Trash2, ExternalLink, X, Save, Clock, FileText, Truck, Upload, MoreVertical, Edit2, Eye, Clipboard, Image, MapPin, Package, CheckCircle, XCircle, Calendar, Activity } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';
import { colors } from '../theme';
import ModalHistoricoPedido from './ModalHistoricoPedido';
import ModalComprovanteCorreios from './ModalComprovanteCorreios';
import ModalConfirmacaoEntrega from './ModalConfirmacaoEntrega';

// Helper para obter URL do comprovante (suporta Base64 e URLs relativas)
const getComprovanteUrl = (url) => {
  if (!url) return null;
  // Se já é uma data URL (Base64), retorna diretamente
  if (url.startsWith('data:')) return url;
  // Se é URL relativa, adiciona o API prefix
  return `${API}${url}`;
};

const ConsultarPedidos = ({ user }) => {
  const [pedidos, setPedidos] = useState([]);
  const [transportadoras, setTransportadoras] = useState([]);
  const [vendedores, setVendedores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [buscaGlobal, setBuscaGlobal] = useState('');
  const [buscaInput, setBuscaInput] = useState('');
  const intervalRef = useRef(null);
  // Refs para manter valores atualizados no polling
  const paginaAtualRef = useRef(1);
  const buscaGlobalRef = useRef('');
  const filtroStatusRef = useRef('TODOS');
  const filtroTransportadoraRef = useRef('TODAS');
  
  const [editingPedido, setEditingPedido] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showEditModal, setShowEditModal] = useState(false);
  const [isViewOnlyMode, setIsViewOnlyMode] = useState(false);
  const [historicoModal, setHistoricoModal] = useState({ isOpen: false, pedido: null });
  const [correiosModal, setCorreiosModal] = useState({ isOpen: false, pedido: null });
  const [rastreamentoModal, setRastreamentoModal] = useState({ isOpen: false, pedido: null, dados: null, loading: false });
  const [ediModal, setEdiModal] = useState({ isOpen: false, pedido: null, dados: null, loading: false });
  const [atualizandoStatus, setAtualizandoStatus] = useState(false);
  const [modalEntrega, setModalEntrega] = useState({ isOpen: false, pedido: null, dataPreenchida: null });
  const [linkCopiado, setLinkCopiado] = useState(false);
  const [filtroStatus, setFiltroStatus] = useState('TODOS');
  const [filtroTransportadora, setFiltroTransportadora] = useState('TODAS');
  const [statusOptions, setStatusOptions] = useState([]);
  const [uploadingComprovante, setUploadingComprovante] = useState(false);
  
  // Paginação do backend
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [totalPedidos, setTotalPedidos] = useState(0);
  const [itensPorPagina] = useState(100);
  
  // Estatísticas do backend (gerais - não mudam com busca)
  const [estatisticasGerais, setEstatisticasGerais] = useState({
    total_geral: 0,
    entregues: 0,
    nao_entregues: 0
  });
  const [estatisticasCarregadas, setEstatisticasCarregadas] = useState(false);
  
  // Busca só dispara no ENTER ou clique — sem debounce automático

  // Atualizar refs quando valores mudarem (para uso no polling)
  useEffect(() => {
    buscaGlobalRef.current = buscaGlobal;
  }, [buscaGlobal]);
  
  useEffect(() => {
    filtroStatusRef.current = filtroStatus;
  }, [filtroStatus]);
  
  useEffect(() => {
    filtroTransportadoraRef.current = filtroTransportadora;
  }, [filtroTransportadora]);
  
  useEffect(() => {
    paginaAtualRef.current = paginaAtual;
  }, [paginaAtual]);

  // Recarregar quando filtros de STATUS ou TRANSPORTADORA mudarem (exceto na primeira carga)
  // A busca (buscaGlobal) é controlada diretamente pelas funções executarBusca/handleBuscaGlobal
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  
  useEffect(() => {
    if (initialLoadDone) {
      loadPedidos(1, buscaGlobal, filtroStatus, filtroTransportadora);
    }
  }, [filtroStatus, filtroTransportadora]); // Removido buscaGlobal - controlado diretamente

  // Carregar dados iniciais UMA VEZ
  useEffect(() => {
    console.log('🔄 Consultar Pedidos - Iniciando');
    
    // Carregar dados inicial
    loadInitialData();
    
    // Atualizar a cada 60 segundos (usa refs para valores atualizados) - silencioso
    intervalRef.current = setInterval(() => {
      console.log('🔄 ConsultarPedidos - Atualizando automaticamente');
      loadPedidos(paginaAtualRef.current, buscaGlobalRef.current, filtroStatusRef.current, filtroTransportadoraRef.current, false);
    }, 60000); // 60 segundos - atualização silenciosa
    
    // Limpar interval quando desmontar
    return () => {
      if (intervalRef.current) {
        console.log('🛑 Consultar Pedidos - Parando atualização automática');
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  // Carregar dados iniciais (transportadoras, status, vendedores)
  const loadInitialData = async () => {
    try {
      const token = localStorage.getItem('primaza_token');
      
      // Carregar pedidos PRIMEIRO (mais importante) em paralelo com os outros dados
      const [pedidosPromise, transportadorasRes, statusRes] = await Promise.all([
        loadPedidos(1, '', 'TODOS', 'TODAS'),
        axios.get(`${API}/transportadoras`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/status-pedido`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }))
      ]);
      
      setTransportadoras(transportadorasRes.data || []);
      
      const statusAtivos = (statusRes.data || []).filter(s => s.ativo).map(s => s.nome);
      setStatusOptions(statusAtivos.length > 0 ? statusAtivos : ['FATURADO', 'IMPRESSO', 'EMBALADO', 'ENVIADO', 'ENTREGUE', 'EM TRÂNSITO', 'PENDENTE']);
      
      // Carregar vendedores em background (não bloqueia)
      if (user.role === 'admin' || user.role === 'lancador') {
        axios.get(`${API}/vendedores`).then(res => setVendedores(res.data)).catch(() => {});
      }
      
      setInitialLoadDone(true);
    } catch (error) {
      console.error('Erro ao carregar dados iniciais:', error);
      setStatusOptions(['FATURADO', 'IMPRESSO', 'EMBALADO', 'ENVIADO', 'ENTREGUE', 'EM TRÂNSITO', 'PENDENTE']);
      setInitialLoadDone(true);
    }
  };

  // Carregar pedidos com paginação do backend (com retry automático)
  const loadPedidos = async (page = 1, busca = '', status = 'TODOS', transportadora = 'TODAS', showLoading = true, retryCount = 0) => {
    if (showLoading) {
      setLoading(true);
    }
    try {
      const token = localStorage.getItem('primaza_token');
      
      if (!token) {
        console.error('Token não encontrado');
        setLoading(false);
        return;
      }
      
      // Construir query params
      const params = new URLSearchParams({
        page: page.toString(),
        limit: itensPorPagina.toString()
      });
      
      if (busca) params.append('busca', busca);
      if (status && status !== 'TODOS') params.append('status', status);
      if (transportadora && transportadora !== 'TODAS') params.append('transportadora', transportadora);
      
      const response = await axios.get(`${API}/pedidos?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 30000  // 30 segundos de timeout
      });
      
      const { pedidos: pedidosData, total, total_pages, estatisticas: stats } = response.data;
      
      setPedidos(pedidosData);
      setTotalPedidos(total);
      setTotalPaginas(total_pages);
      setPaginaAtual(page);
      
      // Atualizar estatísticas gerais apenas na primeira carga (sem filtros)
      if (stats && !estatisticasCarregadas && !busca && status === 'TODOS' && transportadora === 'TODAS') {
        setEstatisticasGerais(stats);
        setEstatisticasCarregadas(true);
      }
      
      console.log(`✅ Pedidos carregados: ${pedidosData.length} de ${total}`);
    } catch (error) {
      console.error('Erro ao carregar pedidos:', error);
      
      // Retry automático (máximo 2 tentativas)
      if (retryCount < 2) {
        console.log(`🔄 Tentando novamente (${retryCount + 1}/2)...`);
        setTimeout(() => {
          loadPedidos(page, busca, status, transportadora, showLoading, retryCount + 1);
        }, 1000);
        return;
      }
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  // Usar estatísticas GERAIS do backend (fixas, não mudam com busca)
  const calcularEstatisticas = () => {
    return { 
      total: estatisticasGerais.total_geral, 
      entregues: estatisticasGerais.entregues, 
      naoEntregues: estatisticasGerais.nao_entregues 
    };
  };

  // Navegação de página (agora chama o backend)
  const irParaPagina = (numeroPagina) => {
    loadPedidos(numeroPagina, buscaGlobal, filtroStatus, filtroTransportadora);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const buscarPedidos = (termo) => {
    setBuscaInput(termo);
  };

  // Executa a busca - chamada pelo Enter ou clique no botão
  const executarBusca = () => {
    console.log('🔍 Executando busca:', buscaInput);
    setBuscaGlobal(buscaInput);
    setPaginaAtual(1);
    loadPedidos(1, buscaInput, filtroStatus, filtroTransportadora);
  };

  // Atualiza o campo de input enquanto digita (sem executar busca)
  const handleBuscaGlobal = (e) => {
    setBuscaInput(e.target.value);
  };

  // Executa busca ao pressionar Enter
  const handleBuscaKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      executarBusca();
    }
  };

  // Limpa todos os filtros
  const limparFiltros = () => {
    setBuscaInput('');
    setBuscaGlobal('');
    setFiltroTransportadora('TODAS');
    setFiltroStatus('TODOS');
    setPaginaAtual(1);
    loadPedidos(1, '', 'TODOS', 'TODAS');
  };

  const handleFiltroStatus = (status) => {
    setFiltroStatus(status);
    setPaginaAtual(1);
    loadPedidos(1, buscaGlobal, status, filtroTransportadora);
  };
  
  const handleFiltroTransportadora = (transportadora) => {
    setFiltroTransportadora(transportadora);
    setPaginaAtual(1);
    loadPedidos(1, buscaGlobal, filtroStatus, transportadora);
  };

  const handleEdit = (pedido) => {
    setEditingPedido(pedido);
    // Formatar prev_entrega para YYYY-MM-DD (input date)
    let prevEntrega = pedido.prev_entrega || '';
    if (prevEntrega && prevEntrega.includes('T')) {
      prevEntrega = prevEntrega.split('T')[0];
    }
    setEditForm({
      pedido: pedido.pedido || '',
      vendedor: pedido.vendedor || '',
      nfe: pedido.nfe || '',
      chave_nfe: pedido.chave_nfe || '',
      cod_cliente: pedido.cod_cliente || '',
      nome_cliente: pedido.nome_cliente || '',
      email_cliente: pedido.email_cliente || '',
      status_pedido: pedido.status_pedido || '',
      prev_entrega: prevEntrega,
      transportadora: pedido.transportadora || '',
      cod_rastreamento: pedido.cod_rastreamento || '',
      valor_frete: pedido.valor_frete ? String(pedido.valor_frete) : '',
      // Campos CTE
      numero_cte: pedido.numero_cte || '',
      chave_cte: pedido.chave_cte || '',
      valor_frete_cte: pedido.valor_frete_cte ? String(pedido.valor_frete_cte) : '',
      data_emissao_cte: pedido.data_emissao_cte || '',
      tipo_frete: pedido.tipo_frete || '',
      // Comprovantes
      comprovante_correios_url: pedido.comprovante_correios_url || '',
      comprovante_entrega_url: pedido.comprovante_entrega_url || '',
      data_entrega_real: pedido.data_entrega_real || ''
    });
    setIsViewOnlyMode(false);
    setShowEditModal(true);
  };
  
  // Função para visualizar em modo somente leitura (para vendedores)
  const handleViewDetails = (pedido) => {
    setEditingPedido(pedido);
    let prevEntrega2 = pedido.prev_entrega || '';
    if (prevEntrega2 && prevEntrega2.includes('T')) {
      prevEntrega2 = prevEntrega2.split('T')[0];
    }
    setEditForm({
      pedido: pedido.pedido || '',
      vendedor: pedido.vendedor || '',
      nfe: pedido.nfe || '',
      chave_nfe: pedido.chave_nfe || '',
      cod_cliente: pedido.cod_cliente || '',
      nome_cliente: pedido.nome_cliente || '',
      email_cliente: pedido.email_cliente || '',
      status_pedido: pedido.status_pedido || '',
      prev_entrega: prevEntrega2,
      transportadora: pedido.transportadora || '',
      cod_rastreamento: pedido.cod_rastreamento || '',
      valor_frete: pedido.valor_frete ? String(pedido.valor_frete) : '',
      // Campos CTE
      numero_cte: pedido.numero_cte || '',
      chave_cte: pedido.chave_cte || '',
      valor_frete_cte: pedido.valor_frete_cte ? String(pedido.valor_frete_cte) : '',
      data_emissao_cte: pedido.data_emissao_cte || '',
      tipo_frete: pedido.tipo_frete || '',
      // Comprovantes
      comprovante_correios_url: pedido.comprovante_correios_url || '',
      comprovante_entrega_url: pedido.comprovante_entrega_url || '',
      data_entrega_real: pedido.data_entrega_real || ''
    });
    setIsViewOnlyMode(true);
    setShowEditModal(true);
  };

  // Função para processar o upload do arquivo (usada tanto por input quanto por paste)
  const processUploadFile = async (file) => {
    if (!file) return;

    // Validar tipo de arquivo
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      alert('Apenas arquivos JPG, PNG ou PDF são permitidos');
      return;
    }

    // Validar tamanho (máx 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Arquivo muito grande. Máximo 5MB');
      return;
    }

    setUploadingComprovante(true);
    try {
      const token = localStorage.getItem('primaza_token');
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post(
        `${API}/pedidos/${editingPedido.id}/upload-comprovante`,
        formDataUpload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      // Atualizar o formulário com a nova URL
      setEditForm({ ...editForm, comprovante_entrega_url: response.data.url });
      alert('Comprovante atualizado com sucesso!');
      
    } catch (error) {
      console.error('Erro ao fazer upload:', error);
      alert(error.response?.data?.detail || 'Erro ao fazer upload do arquivo');
    } finally {
      setUploadingComprovante(false);
    }
  };

  // Função para upload/troca do comprovante de entrega (via input file)
  const handleUploadComprovanteEntrega = async (e) => {
    const file = e.target.files[0];
    await processUploadFile(file);
  };

  // Função para colar imagem do clipboard (Ctrl+V)
  const handlePasteComprovante = async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      
      // Verificar se é uma imagem
      if (item.type.indexOf('image') !== -1) {
        e.preventDefault();
        const file = item.getAsFile();
        
        if (file) {
          // Renomear o arquivo para ter uma extensão adequada
          const extension = item.type.split('/')[1] || 'png';
          const newFile = new File([file], `comprovante_${Date.now()}.${extension}`, {
            type: item.type
          });
          
          await processUploadFile(newFile);
        }
        break;
      }
    }
  };

  const handleSaveEdit = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/pedidos/${editingPedido.id}`, editForm);
      
      // Fechar modal
      setShowEditModal(false);
      setEditingPedido(null);
      
      // Recarregar dados sem loading spinner para atualização suave
      await loadPedidos(paginaAtual, buscaGlobal, filtroStatus, filtroTransportadora, false);
      
      alert('Pedido atualizado com sucesso!');
    } catch (error) {
      console.error('Erro ao atualizar pedido:', error);
      alert('Erro ao atualizar pedido');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (pedidoId) => {
    if (!window.confirm('Tem certeza que deseja excluir este pedido?')) {
      return;
    }

    setLoading(true);
    try {
      await axios.delete(`${API}/pedidos/${pedidoId}`);
      loadPedidos(paginaAtual, buscaGlobal, filtroStatus, filtroTransportadora);
    } catch (error) {
      console.error('Erro ao deletar pedido:', error);
      alert('Erro ao deletar pedido');
    } finally {
      setLoading(false);
    }
  };

  // Função para buscar rastreamento nos Correios
  const abrirModalConfirmacaoEntrega = () => {
    if (!rastreamentoModal.pedido || !rastreamentoModal.dados) return;
    
    // Converter a data dos Correios para formato YYYY-MM-DD
    let dataEntrega = '';
    if (rastreamentoModal.dados.ultima_data) {
      try {
        // A data vem no formato ISO: "2024-12-20T15:30:00"
        const dataISO = rastreamentoModal.dados.ultima_data;
        dataEntrega = dataISO.split('T')[0]; // Pega apenas a parte da data (YYYY-MM-DD)
      } catch (e) {
        console.error('Erro ao converter data:', e);
      }
    }
    
    // Fechar modal de rastreamento e abrir modal de confirmação
    setRastreamentoModal({ isOpen: false, pedido: null, dados: null, loading: false });
    setModalEntrega({ 
      isOpen: true, 
      pedido: rastreamentoModal.pedido, 
      dataPreenchida: dataEntrega 
    });
  };

  const compartilharWhatsApp = () => {
    if (!rastreamentoModal.pedido?.cod_rastreamento) return;
    
    const codigo = rastreamentoModal.pedido.cod_rastreamento.replace(/\s/g, '');
    const pedidoNum = rastreamentoModal.pedido.pedido || '';
    const linkPublico = `https://www.rastreamentoprimaza.com.br/${pedidoNum}`;
    
    const status = rastreamentoModal.dados?.ultimo_status || 'Em trânsito';
    
    const mensagem = `Olá! 👋

Seu pedido *#${pedidoNum}* está a caminho!

📦 *Status:* ${status}
🔍 *Código:* ${codigo}

Acompanhe em tempo real:
${linkPublico}

🌿 _Primaza Rastreamento Animal_
_Do brinco até você_`;
    
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(mensagem)}`;
    window.open(whatsappUrl, '_blank');
  };

  const copiarLinkPublico = async () => {
    if (!rastreamentoModal.pedido?.cod_rastreamento) return;
    
    const pedidoNum = rastreamentoModal.pedido.pedido || '';
    const linkPublico = `https://www.rastreamentoprimaza.com.br/${pedidoNum}`;
    
    try {
      await navigator.clipboard.writeText(linkPublico);
      setLinkCopiado(true);
      setTimeout(() => setLinkCopiado(false), 3000);
    } catch (err) {
      // Fallback para navegadores antigos
      const textArea = document.createElement('textarea');
      textArea.value = linkPublico;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setLinkCopiado(true);
      setTimeout(() => setLinkCopiado(false), 3000);
    }
  };

  // Funções de compartilhamento para API São Miguel
  const compartilharEdiWhatsApp = () => {
    if (!ediModal.pedido?.nfe) return;
    
    const pedidoNum = ediModal.pedido.pedido || '';
    const nfeNum = ediModal.pedido.nfe?.replace(/^0+/, '') || '';
    const linkPublico = `https://www.rastreamentoprimaza.com.br/${pedidoNum}`;
    
    const status = ediModal.dados?.ultimo_status || 'Em trânsito';
    const transportadora = ediModal.pedido.transportadora || 'Expresso São Miguel';
    
    const mensagem = `Olá! 👋

Seu pedido *#${pedidoNum}* está a caminho!

📦 *Status:* ${status}
🚚 *Transportadora:* ${transportadora}
🔍 *NF:* ${nfeNum}

Acompanhe em tempo real:
${linkPublico}

🌿 _Primaza Rastreamento Animal_
_Do brinco até você_`;
    
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(mensagem)}`;
    window.open(whatsappUrl, '_blank');
  };

  const copiarLinkEdi = async () => {
    if (!ediModal.pedido?.nfe) return;
    
    const pedidoNum = ediModal.pedido.pedido || '';
    const linkPublico = `https://www.rastreamentoprimaza.com.br/${pedidoNum}`;
    
    try {
      await navigator.clipboard.writeText(linkPublico);
      setLinkCopiado(true);
      setTimeout(() => setLinkCopiado(false), 3000);
    } catch (err) {
      const textArea = document.createElement('textarea');
      textArea.value = linkPublico;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setLinkCopiado(true);
      setTimeout(() => setLinkCopiado(false), 3000);
    }
  };

  // Função helper para determinar se deve sugerir atualização de status
  const devesugerirAtualizacao = () => {
    if (!rastreamentoModal.dados || !rastreamentoModal.pedido) return null;
    
    const statusPedido = rastreamentoModal.pedido.status_pedido || '';
    const statusCorreios = rastreamentoModal.dados.ultimo_status || '';
    const entregue = rastreamentoModal.dados.entregue;
    
    // Se foi entregue nos Correios mas não está marcado como ENTREGUE no sistema
    if (entregue && statusPedido !== 'ENTREGUE') {
      return {
        sugerir: true,
        statusSugerido: 'ENTREGUE',
        mensagem: 'Os Correios confirmam: "Objeto entregue ao destinatário"',
        cor: 'green'
      };
    }
    
    // Se está saiu para entrega mas status atual é anterior
    if (statusCorreios.toLowerCase().includes('saiu para entrega') && 
        !['ENTREGUE', 'EM TRÂNSITO'].includes(statusPedido)) {
      return {
        sugerir: true,
        statusSugerido: 'EM TRÂNSITO',
        mensagem: `Correios indicam: "${statusCorreios}"`,
        cor: 'blue'
      };
    }
    
    // Se foi postado mas status atual é anterior
    if (statusCorreios.toLowerCase().includes('postado') && 
        !['ENTREGUE', 'EM TRÂNSITO', 'POSTADO'].includes(statusPedido)) {
      return {
        sugerir: true,
        statusSugerido: 'POSTADO',
        mensagem: `Correios indicam: "${statusCorreios}"`,
        cor: 'yellow'
      };
    }
    
    return null;
  };

  const buscarRastreamento = async (pedido) => {
    
    setRastreamentoModal({ isOpen: true, pedido, dados: null, loading: true });
    
    try {
      const token = localStorage.getItem('primaza_token');
      const response = await axios.get(
        `${API}/correios/rastrear/${pedido.cod_rastreamento}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setRastreamentoModal(prev => ({ ...prev, dados: response.data, loading: false }));
    } catch (error) {
      console.error('Erro ao buscar rastreamento:', error);
      setRastreamentoModal(prev => ({ 
        ...prev, 
        dados: { erro: error.response?.data?.detail || 'Erro ao buscar rastreamento' }, 
        loading: false 
      }));
    }
  };

  // Função para buscar dados São Miguel - consulta API em tempo real + dados salvos
  const buscarDadosEDI = async (pedido) => {
    if (!pedido.nfe) {
      alert('Este pedido não possui número de NF');
      return;
    }
    
    const token = localStorage.getItem('primaza_token');
    const headers = { Authorization: `Bearer ${token}` };
    const isSM = isSaoMiguel(pedido.transportadora);
    const isBP = isBraspress(pedido.transportadora);

    // Abrir modal com loading (igual para todas as transportadoras)
    setEdiModal({ isOpen: true, pedido, dados: null, loading: true });
    
    try {
      let apiData = null;

      if (isSM) {
        // 1. Consultar API em tempo real (atualiza banco + busca comprovante)
        try {
          const liveRes = await axios.post(
            `${API}/esm/atualizar-pedido/${pedido.pedido}`, {},
            { headers, timeout: 30000 }
          );
          if (liveRes.data?.atualizado && liveRes.data?.dados) {
            apiData = liveRes.data.dados;
          }
        } catch (liveErr) {
          console.warn('Consulta API em tempo real falhou, usando dados salvos:', liveErr.message);
        }

        // 2. Se a consulta em tempo real falhou, usar dados já salvos
        if (!apiData) {
          try {
            const savedRes = await axios.get(`${API}/esm/tracking/${pedido.pedido}`, { headers });
            apiData = savedRes.data;
          } catch (e) {
            console.warn('Dados salvos também indisponíveis');
          }
        }
      } else if (isBP) {
        // Braspress: mesmo fluxo - consultar API em tempo real
        try {
          const liveRes = await axios.post(
            `${API}/braspress/atualizar-pedido/${pedido.pedido}`, {},
            { headers, timeout: 25000 }
          );
          const resData = liveRes.data;
          if (resData?.dados) {
            apiData = {
              status_pedido: resData.status || resData.dados?.status_pedido,
              api_tracking_eventos: resData.dados?.api_tracking_eventos || [],
              cte_transportadora: resData.dados?.cte_transportadora,
              frete_transportadora: resData.dados?.frete_transportadora,
              valor_frete: resData.dados?.valor_frete,
              previsao_entrega_transportadora: resData.dados?.previsao_entrega_transportadora || resData.dados?.prev_entrega,
              tipo_frete: resData.dados?.tipo_frete,
              origem_transportadora: resData.dados?.origem_transportadora,
              destino_transportadora: resData.dados?.destino_transportadora,
              data_entrega_transportadora: resData.dados?.data_entrega_transportadora,
              peso_transportadora: resData.dados?.peso_transportadora,
              volumes_transportadora: resData.dados?.volumes_transportadora,
              remetente_transportadora: resData.dados?.remetente_transportadora,
              destinatario_transportadora: resData.dados?.destinatario_transportadora,
              nome_cliente: resData.dados?.nome_cliente || pedido.nome_cliente,
              transportadora: resData.dados?.transportadora || pedido.transportadora,
            };
          }
        } catch (liveErr) {
          console.warn('Consulta Braspress API falhou, usando dados salvos:', liveErr.message);
        }

        // Fallback: usar dados do pedido se API falhar
        if (!apiData) {
          apiData = {
            status_pedido: pedido.status_pedido,
            api_tracking_eventos: pedido.api_tracking_eventos || [],
            cte_transportadora: pedido.cte_transportadora,
            frete_transportadora: pedido.frete_transportadora,
            valor_frete: pedido.valor_frete,
            previsao_entrega_transportadora: pedido.previsao_entrega_transportadora || pedido.prev_entrega,
            tipo_frete: pedido.tipo_frete,
            origem_transportadora: pedido.origem_transportadora,
            destino_transportadora: pedido.destino_transportadora,
            data_entrega_transportadora: pedido.data_entrega_transportadora,
            peso_transportadora: pedido.peso_transportadora,
            volumes_transportadora: pedido.volumes_transportadora,
            remetente_transportadora: pedido.remetente_transportadora,
            destinatario_transportadora: pedido.destinatario_transportadora,
            nome_cliente: pedido.nome_cliente,
            transportadora: pedido.transportadora,
          };
        }
      } else if (isRodonavez(pedido.transportadora)) {
        // Rodonaves: mesmo fluxo - consultar API em tempo real
        try {
          const liveRes = await axios.post(
            `${API}/rodonaves/atualizar-pedido/${pedido.pedido}`, {},
            { headers, timeout: 25000 }
          );
          const resData = liveRes.data;
          if (resData?.dados) {
            apiData = {
              status_pedido: resData.status || resData.dados?.status_pedido,
              api_tracking_eventos: resData.dados?.api_tracking_eventos || [],
              cte_transportadora: resData.dados?.cte_transportadora,
              previsao_entrega_transportadora: resData.dados?.previsao_entrega_transportadora,
              remetente_transportadora: resData.dados?.remetente_transportadora,
              destinatario_transportadora: resData.dados?.destinatario_transportadora,
              origem_transportadora: resData.dados?.origem_transportadora,
              data_entrega_transportadora: resData.dados?.data_entrega_transportadora,
              nome_cliente: resData.dados?.nome_cliente || pedido.nome_cliente,
              transportadora: resData.dados?.transportadora || pedido.transportadora,
            };
          }
        } catch (liveErr) {
          console.warn('Consulta Rodonaves API falhou, usando dados salvos:', liveErr.message);
        }

        // Fallback
        if (!apiData) {
          apiData = {
            status_pedido: pedido.status_pedido,
            api_tracking_eventos: pedido.api_tracking_eventos || [],
            cte_transportadora: pedido.cte_transportadora,
            previsao_entrega_transportadora: pedido.previsao_entrega_transportadora,
            remetente_transportadora: pedido.remetente_transportadora,
            destinatario_transportadora: pedido.destinatario_transportadora,
            origem_transportadora: pedido.origem_transportadora,
            data_entrega_transportadora: pedido.data_entrega_transportadora,
            nome_cliente: pedido.nome_cliente,
            transportadora: pedido.transportadora,
          };
        }
      }

      // Montar dados do modal com dados da API
      const dados = {
        api_tracking_eventos: apiData?.api_tracking_eventos || [],
        previsao_entrega_oficial: apiData?.previsao_entrega_oficial || null,
        comprovante_entrega: apiData?.comprovante_entrega || null,
        ultimo_tracking_api: apiData?.ultimo_tracking_api || null,
        valor_frete_api: apiData?.valor_frete_api || null,
        cte_transportadora: apiData?.cte_transportadora || null,
        chave_cte_transportadora: apiData?.chave_cte_transportadora || null,
        frete_transportadora: apiData?.frete_transportadora || null,
        valor_mercadoria_transportadora: apiData?.valor_mercadoria_transportadora || null,
        peso_m3_transportadora: apiData?.peso_m3_transportadora || null,
        previsao_entrega_transportadora: apiData?.previsao_entrega_transportadora || null,
        origem_transportadora: apiData?.origem_transportadora || null,
        destino_transportadora: apiData?.destino_transportadora || null,
        endereco_entrega_transportadora: apiData?.endereco_entrega_transportadora || null,
        unidade_destino_transportadora: apiData?.unidade_destino_transportadora || null,
        data_entrega_transportadora: apiData?.data_entrega_transportadora || null,
        lat_entrega_transportadora: apiData?.lat_entrega_transportadora || null,
        lng_entrega_transportadora: apiData?.lng_entrega_transportadora || null,
        tipo_frete: apiData?.tipo_frete || null,
        peso_transportadora: apiData?.peso_transportadora || null,
        volumes_transportadora: apiData?.volumes_transportadora || null,
        remetente_transportadora: apiData?.remetente_transportadora || null,
        destinatario_transportadora: apiData?.destinatario_transportadora || null,
        status_pedido: apiData?.status_pedido || null,
        nome_cliente: apiData?.nome_cliente || pedido.nome_cliente,
        transportadora: apiData?.transportadora || pedido.transportadora,
      };
      
      setEdiModal(prev => ({ ...prev, dados, loading: false }));
    } catch (error) {
      console.error('Erro ao buscar dados:', error);
      setEdiModal(prev => ({ 
        ...prev, 
        dados: { erro: error.response?.data?.detail || 'Nenhuma ocorrência encontrada para esta NF' }, 
        loading: false 
      }));
    }
  };

  // Verificar se transportadora é São Miguel
  const isSaoMiguel = (transportadora) => {
    if (!transportadora) return false;
    return transportadora.toLowerCase().includes('miguel') || 
           transportadora.toLowerCase().includes('são miguel') ||
           transportadora.toLowerCase().includes('sao miguel');
  };

  // Verificar se transportadora é Braspress
  const isBraspress = (transportadora) => {
    if (!transportadora) return false;
    return transportadora.toLowerCase().includes('braspress');
  };

  // Verificar se transportadora é Rodonaves
  const isRodonavez = (transportadora) => {
    if (!transportadora) return false;
    return transportadora.toLowerCase().includes('rodonaves') || transportadora.toLowerCase().includes('rodonavez');
  };

  // Verificar se transportadora é Correios ou usa rastreio Correios (LUPA)
  const isCorreios = (transportadora) => {
    if (!transportadora) return false;
    const t = transportadora.toLowerCase();
    return t.includes('correios') || t.includes('lupa');
  };

  // Função unificada de rastreio para mobile — chama a API correta por transportadora
  const rastrearMobile = (pedido) => {
    const t = pedido.transportadora || '';
    if (isCorreios(t)) {
      if (!pedido.cod_rastreamento) { alert('Pedido sem código de rastreamento'); return; }
      buscarRastreamento(pedido);
    } else {
      buscarDadosEDI(pedido);
    }
  };

  // Cor do caminhão por transportadora
  const getTruckColor = (pedido) => {
    const t = pedido.transportadora || '';
    if (isBraspress(t)) return 'text-orange-600 hover:text-orange-800';
    if (isSaoMiguel(t)) return 'text-green-600 hover:text-green-800';
    if (isRodonavez(t)) return 'text-blue-600 hover:text-blue-800';
    if (isCorreios(t)) return 'text-yellow-600 hover:text-yellow-800';
    return 'text-gray-600 hover:text-gray-800';
  };

  const gerarLinkRastreamento = (pedido) => {
    if (!pedido.cod_rastreamento) return null;
    
    // Se tem transportadora, tentar encontrar URL específica
    if (pedido.transportadora) {
      const transportadora = transportadoras.find(t => 
        t.nome.toLowerCase().includes(pedido.transportadora.toLowerCase())
      );
      
      if (transportadora && transportadora.url_rastreamento) {
        let url = transportadora.url_rastreamento;
        url = url.replace('{{code}}', pedido.cod_rastreamento);
        url = url.replace('{{chave}}', pedido.chave_nfe || '');
        return url;
      }
    }
    
    // Fallback: Se for Correios/LUPA usa URL dos Correios
    if (isCorreios(pedido.transportadora)) {
      return 'https://rastreamento.correios.com.br/app/index.php';
    }
    
    // Sem URL disponível
    return null;
  };

  const getStatusColor = (status) => {
    const colors = {
      'ENTREGUE': 'bg-green-100 text-green-800 border-green-300',
      'FATURADO': 'bg-blue-100 text-blue-800 border-blue-300',
      'IMPRESSO': 'bg-yellow-100 text-yellow-800 border-yellow-300',
      'EMBALADO': 'bg-purple-100 text-purple-800 border-purple-300',
      'ENVIADO PARA PRODUÇÃO': 'bg-orange-100 text-orange-800 border-orange-300',
      'EM TRÂNSITO': 'bg-indigo-100 text-indigo-800 border-indigo-300',
      'PENDENTE': 'bg-gray-100 text-gray-800 border-gray-300'
    };
    return colors[status] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const canEditOrDelete = user.role === 'admin' || user.role === 'lancador';
  const canViewHistory = true; // Todos os usuários podem ver histórico

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Page Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold" style={{ color: colors.primaza.secondary }}>
              Consultar Pedidos
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              Visualize, edite e gerencie todos os pedidos
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => loadPedidos(paginaAtual, buscaGlobal, filtroStatus)}
            disabled={loading}
            style={{ 
              borderColor: colors.primaza.primary,
              color: colors.primaza.secondary 
            }}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
        </div>
      </div>

      <div className="max-w-full mx-auto p-6 space-y-6">
        {/* Cards de Estatísticas */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Card Total */}
          <Card 
            className={`cursor-pointer transition-all hover:shadow-lg ${filtroStatus === 'TODOS' ? 'ring-2 ring-blue-500 bg-blue-50' : ''}`}
            onClick={() => handleFiltroStatus('TODOS')}
          >
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total de Pedidos</p>
                  <p className="text-3xl font-bold text-gray-900 mt-2">{calcularEstatisticas().total}</p>
                </div>
                <div className="p-3 bg-blue-100 rounded-full">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
              </div>
              {filtroStatus === 'TODOS' && (
                <p className="text-xs text-blue-600 mt-2">✓ Filtro ativo</p>
              )}
            </CardContent>
          </Card>

          {/* Card Entregues */}
          <Card 
            className={`cursor-pointer transition-all hover:shadow-lg ${filtroStatus === 'ENTREGUE' ? 'ring-2 ring-green-500 bg-green-50' : ''}`}
            onClick={() => handleFiltroStatus('ENTREGUE')}
          >
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Entregues</p>
                  <p className="text-3xl font-bold text-green-600 mt-2">{calcularEstatisticas().entregues}</p>
                </div>
                <div className="p-3 bg-green-100 rounded-full">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>
              {filtroStatus === 'ENTREGUE' && (
                <p className="text-xs text-green-600 mt-2">✓ Filtro ativo</p>
              )}
            </CardContent>
          </Card>

          {/* Card Não Entregues */}
          <Card 
            className={`cursor-pointer transition-all hover:shadow-lg ${filtroStatus === 'NAO_ENTREGUE' ? 'ring-2 ring-orange-500 bg-orange-50' : ''}`}
            onClick={() => handleFiltroStatus('NAO_ENTREGUE')}
          >
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Não Entregues</p>
                  <p className="text-3xl font-bold text-orange-600 mt-2">{calcularEstatisticas().naoEntregues}</p>
                </div>
                <div className="p-3 bg-orange-100 rounded-full">
                  <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
              {filtroStatus === 'NAO_ENTREGUE' && (
                <p className="text-xs text-orange-600 mt-2">✓ Filtro ativo</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Busca Global */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Search size={20} style={{ color: colors.primaza.primary }} />
              Busca Rápida
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col md:flex-row gap-3">
              <div className="flex flex-1 gap-2">
                <Input
                  placeholder="Digite e pressione Enter para buscar..."
                  value={buscaInput}
                  onChange={handleBuscaGlobal}
                  onKeyDown={handleBuscaKeyDown}
                  className="flex-1"
                  data-testid="busca-rapida-input"
                />
                <Button
                  onClick={executarBusca}
                  className="px-4 shrink-0"
                  style={{ background: colors.primaza.primary }}
                  title="Buscar"
                >
                  <Search size={16} />
                </Button>
              </div>
              <select
                value={filtroTransportadora}
                onChange={(e) => handleFiltroTransportadora(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md min-w-[200px]"
                style={{ borderColor: colors.primaza.primary }}
              >
                <option value="TODAS">🚚 Todas Transportadoras</option>
                {transportadoras
                  .filter(t => t.ativa)
                  .sort((a, b) => a.nome.localeCompare(b.nome))
                  .map(transp => (
                    <option key={transp.id} value={transp.nome}>
                      {transp.nome}
                    </option>
                  ))}
              </select>
              {(buscaGlobal || filtroTransportadora !== 'TODAS') && (
                <Button
                  variant="outline"
                  onClick={limparFiltros}
                >
                  Limpar Filtros
                </Button>
              )}
            </div>
            <p className="text-sm text-gray-500 mt-2">
              {pedidos.length} de {totalPedidos} pedidos
            </p>
          </CardContent>
        </Card>

        {/* Tabela de Pedidos */}
        <Card>
          <CardHeader>
            <CardTitle>Lista de Pedidos</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center items-center py-12">
                <Loader2 className="h-8 w-8 animate-spin" style={{ color: colors.primaza.primary }} />
              </div>
            ) : pedidos.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                Nenhum pedido encontrado
              </div>
            ) : (
              <>
                {/* ── Mobile: cards ───────────────────────── */}
                <div className="block md:hidden divide-y divide-gray-100">
                  {pedidos.map((pedido) => (
                    <div
                      key={pedido.id}
                      className="p-4 bg-white active:bg-gray-50 transition-colors"
                    >
                      {/* Linha 1: pedido + status */}
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-semibold text-gray-400">#{pedido.pedido || '-'}</span>
                        <Badge className={`border text-xs ${getStatusColor(pedido.status_pedido)}`}>
                          {pedido.status_pedido || '-'}
                        </Badge>
                      </div>
                      {/* Linha 2: nome cliente */}
                      <p className="font-semibold text-gray-900 text-sm leading-snug mb-1 truncate">
                        {pedido.nome_cliente || '-'}
                      </p>
                      {/* Linha 3: transportadora + NF */}
                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <span className="truncate max-w-[55%]">{pedido.transportadora || '-'}</span>
                        {pedido.nfe && <span className="font-mono">NF {pedido.nfe}</span>}
                      </div>
                      {/* Botões de ação */}
                      {canViewHistory && (
                        <div className="flex gap-2 mt-3">
                          <button
                            onClick={() => setHistoricoModal({ isOpen: true, pedido })}
                            className="flex-1 text-xs py-2 rounded-lg border border-gray-200 bg-gray-50 text-gray-700 font-medium active:scale-95 transition-transform"
                            style={{ minHeight: 36 }}
                            data-testid={`btn-historico-${pedido.id}`}
                          >
                            Histórico
                          </button>
                          <button
                            onClick={() => rastrearMobile(pedido)}
                            className="flex-1 text-xs py-2 rounded-lg font-medium active:scale-95 transition-transform text-white flex items-center justify-center gap-1"
                            style={{ minHeight: 36, backgroundColor: '#1B8B4E' }}
                            data-testid={`btn-rastrear-${pedido.id}`}
                          >
                            Rastrear
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* ── Desktop: tabela ─────────────────────── */}
                <div className="hidden md:block overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Pedido</TableHead>
                      <TableHead>Vendedor</TableHead>
                      <TableHead>Cliente</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Transportadora</TableHead>
                      <TableHead>Rastreamento</TableHead>
                      {(canEditOrDelete || canViewHistory) && <TableHead className="text-right">Ações</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pedidos.map((pedido) => (
                      <TableRow key={pedido.id}>
                        <TableCell className="font-medium">{pedido.pedido || '-'}</TableCell>
                        <TableCell>{pedido.vendedor || '-'}</TableCell>
                        <TableCell>{pedido.nome_cliente || '-'}</TableCell>
                        <TableCell>
                          <Badge className={`border ${getStatusColor(pedido.status_pedido)}`}>
                            {pedido.status_pedido || '-'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span>{pedido.transportadora || '-'}</span>
                            {pedido.tipo_frete && (
                              <Badge 
                                className={`text-xs px-2 py-0.5 ${
                                  pedido.tipo_frete === 'CIF' 
                                    ? 'bg-green-100 text-green-800 border-green-300' 
                                    : 'bg-blue-100 text-blue-800 border-blue-300'
                                }`}
                                title={pedido.tipo_frete === 'CIF' ? 'Frete embutido na NF' : 'Frete separado'}
                              >
                                {pedido.tipo_frete}
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {pedido.cod_rastreamento ? (
                            <div className="flex items-center gap-2">
                              <span 
                                className="text-sm cursor-pointer hover:text-blue-600 hover:underline"
                                onClick={async () => {
                                  try {
                                    await navigator.clipboard.writeText(pedido.cod_rastreamento);
                                    // Feedback visual temporário
                                    const el = document.getElementById(`copy-feedback-${pedido.id}`);
                                    if (el) {
                                      el.textContent = '✓ Copiado!';
                                      el.classList.remove('hidden');
                                      setTimeout(() => el.classList.add('hidden'), 2000);
                                    }
                                  } catch (err) {
                                    // Fallback para navegadores que não suportam clipboard
                                    const textArea = document.createElement('textarea');
                                    textArea.value = pedido.cod_rastreamento;
                                    document.body.appendChild(textArea);
                                    textArea.select();
                                    document.execCommand('copy');
                                    document.body.removeChild(textArea);
                                    const el = document.getElementById(`copy-feedback-${pedido.id}`);
                                    if (el) {
                                      el.textContent = '✓ Copiado!';
                                      el.classList.remove('hidden');
                                      setTimeout(() => el.classList.add('hidden'), 2000);
                                    }
                                  }
                                }}
                                title="Clique para copiar o código"
                              >
                                {pedido.cod_rastreamento}
                              </span>
                              <span id={`copy-feedback-${pedido.id}`} className="hidden text-xs text-green-600 font-medium ml-1">
                                ✓ Copiado!
                              </span>
                              {isCorreios(pedido.transportadora) && pedido.cod_rastreamento && (
                                <button
                                  onClick={() => buscarRastreamento(pedido)}
                                  className={`${getTruckColor(pedido)} transition-colors`}
                                  title="Ver rastreamento Correios"
                                >
                                  <Truck size={18} />
                                </button>
                              )}
                              {/* Botão API São Miguel - na mesma linha */}
                              {isSaoMiguel(pedido.transportadora) && pedido.nfe && (
                                <button
                                  onClick={() => buscarDadosEDI(pedido)}
                                  className={`${getTruckColor(pedido)} transition-colors`}
                                  title="Ver rastreamento API São Miguel"
                                >
                                  <Truck size={18} />
                                </button>
                              )}
                              {/* Botão API Braspress - na mesma linha */}
                              {isBraspress(pedido.transportadora) && pedido.nfe && (
                                <button
                                  onClick={() => buscarDadosEDI(pedido)}
                                  className={`${getTruckColor(pedido)} transition-colors`}
                                  title="Ver rastreamento API Braspress"
                                >
                                  <Truck size={18} />
                                </button>
                              )}
                              {/* Botão API Rodonaves - na mesma linha */}
                              {isRodonavez(pedido.transportadora) && pedido.nfe && (
                                <button
                                  onClick={() => buscarDadosEDI(pedido)}
                                  className={`${getTruckColor(pedido)} transition-colors`}
                                  title="Ver rastreamento API Rodonaves"
                                >
                                  <Truck size={18} />
                                </button>
                              )}
                            </div>
                          ) : (
                            <>
                              {/* Se não tem código de rastreamento, ainda mostra o botão API se for São Miguel */}
                              {isSaoMiguel(pedido.transportadora) && pedido.nfe ? (
                                <button
                                  onClick={() => buscarDadosEDI(pedido)}
                                  className={`${getTruckColor(pedido)} transition-colors`}
                                  title="Ver rastreamento API São Miguel"
                                >
                                  <Truck size={18} />
                                </button>
                              ) : isBraspress(pedido.transportadora) && pedido.nfe ? (
                                <button
                                  onClick={() => buscarDadosEDI(pedido)}
                                  className={`${getTruckColor(pedido)} transition-colors`}
                                  title="Ver rastreamento API Braspress"
                                >
                                  <Truck size={18} />
                                </button>
                              ) : isRodonavez(pedido.transportadora) && pedido.nfe ? (
                                <button
                                  onClick={() => buscarDadosEDI(pedido)}
                                  className={`${getTruckColor(pedido)} transition-colors`}
                                  title="Ver rastreamento API Rodonaves"
                                >
                                  <Truck size={18} />
                                </button>
                              ) : '-'}
                            </>
                          )}
                        </TableCell>
                        {(canEditOrDelete || canViewHistory) && (
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              {/* Ver Detalhes - Botão Verde para Vendedores */}
                              {!canEditOrDelete && canViewHistory && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleViewDetails(pedido)}
                                  style={{ 
                                    borderColor: colors.primaza.primary,
                                    color: colors.primaza.primary 
                                  }}
                                  className="hover:bg-green-50"
                                  title="Ver Detalhes do Pedido"
                                >
                                  <Eye size={16} className="mr-1" />
                                  Ver Detalhes
                                </Button>
                              )}
                              
                              {/* Histórico */}
                              {canViewHistory && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setHistoricoModal({ isOpen: true, pedido })}
                                  className="text-blue-600 hover:bg-blue-50 border-blue-300"
                                  title="Ver Histórico"
                                >
                                  <Clock size={16} />
                                </Button>
                              )}
                              
                              {canEditOrDelete && (
                                <>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleEdit(pedido)}
                                    style={{ 
                                      borderColor: colors.primaza.primary,
                                      color: colors.primaza.secondary 
                                    }}
                                    title="Editar Pedido"
                                  >
                                    <Edit size={16} />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleDelete(pedido.id)}
                                    className="text-red-600 hover:bg-red-50 border-red-300"
                                    title="Excluir Pedido"
                                  >
                                    <Trash2 size={16} />
                                  </Button>
                                </>
                              )}
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
            )}
          </CardContent>
          
          {/* Paginação */}
          {totalPaginas > 1 && (
            <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Página <strong>{paginaAtual}</strong> de <strong>{totalPaginas}</strong>{' '}
                  ({totalPedidos} pedidos)
                </div>
                
                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => irParaPagina(1)}
                    disabled={paginaAtual === 1}
                    variant="outline"
                    size="sm"
                  >
                    Primeira
                  </Button>
                  
                  <Button
                    onClick={() => irParaPagina(paginaAtual - 1)}
                    disabled={paginaAtual === 1}
                    variant="outline"
                    size="sm"
                  >
                    Anterior
                  </Button>
                  
                  <div className="flex items-center gap-2">
                    {/* Mostrar apenas algumas páginas */}
                    {Array.from({ length: Math.min(5, totalPaginas) }, (_, i) => {
                      let numeroPagina;
                      if (totalPaginas <= 5) {
                        numeroPagina = i + 1;
                      } else if (paginaAtual <= 3) {
                        numeroPagina = i + 1;
                      } else if (paginaAtual >= totalPaginas - 2) {
                        numeroPagina = totalPaginas - 4 + i;
                      } else {
                        numeroPagina = paginaAtual - 2 + i;
                      }
                      
                      return (
                        <Button
                          key={numeroPagina}
                          onClick={() => irParaPagina(numeroPagina)}
                          variant={paginaAtual === numeroPagina ? "default" : "outline"}
                          size="sm"
                          style={paginaAtual === numeroPagina ? { backgroundColor: colors.primaza.primary } : {}}
                        >
                          {numeroPagina}
                        </Button>
                      );
                    })}
                  </div>
                  
                  <Button
                    onClick={() => irParaPagina(paginaAtual + 1)}
                    disabled={paginaAtual === totalPaginas}
                    variant="outline"
                    size="sm"
                  >
                    Próxima
                  </Button>
                  
                  <Button
                    onClick={() => irParaPagina(totalPaginas)}
                    disabled={paginaAtual === totalPaginas}
                    variant="outline"
                    size="sm"
                  >
                    Última
                  </Button>
                </div>
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* Modal de Edição */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold" style={{ color: colors.primaza.secondary }}>
                  {isViewOnlyMode ? 'Detalhes do Pedido' : 'Editar Pedido'}
                </h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowEditModal(false);
                    setIsViewOnlyMode(false);
                  }}
                >
                  <X size={20} />
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <Label>Número do Pedido</Label>
                  <Input
                    value={editForm.pedido}
                    onChange={(e) => setEditForm({...editForm, pedido: e.target.value})}
                    className="mt-1"
                    disabled={isViewOnlyMode}
                  />
                </div>
                <div>
                  <Label>Vendedor</Label>
                  <div className="relative mt-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                    <select
                      value={editForm.vendedor}
                      onChange={(e) => setEditForm({...editForm, vendedor: e.target.value})}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md"
                      disabled={isViewOnlyMode}
                    >
                      <option value="">🔍 Buscar vendedor...</option>
                      {vendedores.map(vendedor => (
                        <option key={vendedor.id} value={vendedor.name}>
                          {vendedor.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <Label>NFe</Label>
                  <Input
                    value={editForm.nfe}
                    onChange={(e) => setEditForm({...editForm, nfe: e.target.value})}
                    className="mt-1"
                    disabled={isViewOnlyMode}
                  />
                </div>
                <div>
                  <Label>Chave NFe</Label>
                  <Input
                    value={editForm.chave_nfe}
                    onChange={(e) => setEditForm({...editForm, chave_nfe: e.target.value})}
                    className="mt-1"
                    disabled={isViewOnlyMode}
                  />
                </div>
                <div>
                  <Label>Código Cliente</Label>
                  <Input
                    value={editForm.cod_cliente}
                    onChange={(e) => setEditForm({...editForm, cod_cliente: e.target.value})}
                    className="mt-1"
                    disabled={isViewOnlyMode}
                  />
                </div>
                <div>
                  <Label>Nome Cliente</Label>
                  <Input
                    value={editForm.nome_cliente}
                    onChange={(e) => setEditForm({...editForm, nome_cliente: e.target.value})}
                    className="mt-1"
                    disabled={isViewOnlyMode}
                  />
                </div>
                <div>
                  <Label>E-mail Cliente 📧</Label>
                  <Input
                    type="email"
                    placeholder="cliente@exemplo.com"
                    value={editForm.email_cliente}
                    onChange={(e) => setEditForm({...editForm, email_cliente: e.target.value})}
                    className="mt-1"
                    disabled={isViewOnlyMode}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    E-mail para receber notificações do pedido
                  </p>
                </div>
                <div>
                  <Label>Status</Label>
                  <select
                    value={editForm.status_pedido}
                    onChange={(e) => setEditForm({...editForm, status_pedido: e.target.value})}
                    className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md"
                    disabled={isViewOnlyMode}
                  >
                    {statusOptions.map(status => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>Previsão de Entrega</Label>
                  <Input
                    type="date"
                    value={editForm.prev_entrega}
                    onChange={(e) => setEditForm({...editForm, prev_entrega: e.target.value})}
                    className="mt-1"
                    disabled={isViewOnlyMode}
                  />
                </div>
                <div>
                  <Label>Transportadora</Label>
                  <select
                    value={editForm.transportadora}
                    onChange={(e) => setEditForm({...editForm, transportadora: e.target.value})}
                    className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md"
                    disabled={isViewOnlyMode}
                  >
                    <option value="">Selecione...</option>
                    {transportadoras.filter(t => t.ativa).map(transp => (
                      <option key={transp.id} value={transp.nome}>
                        {transp.nome}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>Código de Rastreamento</Label>
                  <Input
                    value={editForm.cod_rastreamento}
                    onChange={(e) => setEditForm({...editForm, cod_rastreamento: e.target.value})}
                    className="mt-1"
                    disabled={isViewOnlyMode}
                  />
                </div>
                <div>
                  <Label>Valor do Frete</Label>
                  <Input
                    value={editForm.valor_frete}
                    onChange={(e) => setEditForm({...editForm, valor_frete: e.target.value})}
                    className="mt-1"
                    disabled={isViewOnlyMode}
                  />
                </div>
                <div>
                  <Label>Tipo de Frete</Label>
                  <select
                    value={editForm.tipo_frete || ''}
                    onChange={(e) => setEditForm({...editForm, tipo_frete: e.target.value})}
                    className={`w-full px-3 py-2 border rounded-md mt-1 font-semibold ${
                      editForm.tipo_frete === 'FOB' 
                        ? 'border-blue-500 bg-blue-50 text-blue-700' 
                        : editForm.tipo_frete === 'CIF'
                        ? 'border-green-500 bg-green-50 text-green-700'
                        : 'border-gray-300 bg-white text-gray-500'
                    }`}
                    disabled={isViewOnlyMode}
                  >
                    <option value="">Selecione...</option>
                    <option value="CIF">CIF - Primaza paga</option>
                    <option value="FOB">FOB - Cliente paga</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {editForm.tipo_frete === 'CIF' 
                      ? 'Frete conta nos custos Primaza' 
                      : editForm.tipo_frete === 'FOB'
                      ? 'Frete não conta nos custos (cliente paga)'
                      : 'Selecione CIF ou FOB'}
                  </p>
                </div>
              </div>

              {/* CTE - Exibe apenas visualização (importação é feita via página Importações) */}
              {editForm.numero_cte && (
                <div className="mt-6 p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-3">
                    <FileText className="text-blue-600" size={20} />
                    <h3 className="font-bold text-blue-900">CT-e Vinculado</h3>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                    <div className="bg-white p-3 rounded border border-blue-200">
                      <p className="text-gray-600 text-xs mb-1">Número CT-e</p>
                      <p className="font-bold">{editForm.numero_cte}</p>
                    </div>
                    {editForm.valor_frete_cte && (
                      <div className="bg-white p-3 rounded border border-blue-200">
                        <p className="text-gray-600 text-xs mb-1">Valor Frete</p>
                        <p className="font-bold text-green-700">R$ {editForm.valor_frete_cte}</p>
                      </div>
                    )}
                    {editForm.tipo_frete && (
                      <div className="bg-white p-3 rounded border border-blue-200">
                        <p className="text-gray-600 text-xs mb-1">Tipo Frete</p>
                        <Badge className={editForm.tipo_frete === 'CIF' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}>
                          {editForm.tipo_frete}
                        </Badge>
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    📋 Para importar novos CT-es, acesse a página <strong>Importações</strong>
                  </p>
                </div>
              )}

              {/* Comprovante Correios - Para Correios */}
              {editForm.transportadora && editForm.transportadora.toLowerCase() === 'correios' && (
                <div className="mt-6 p-4 bg-yellow-50 border-2 border-yellow-200 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Upload className="text-yellow-600" size={20} />
                      <h3 className="font-bold text-yellow-900">Comprovante de Postagem - Correios</h3>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => setCorreiosModal({ isOpen: true, pedido: editingPedido })}
                      className="bg-yellow-600 hover:bg-yellow-700 text-white"
                    >
                      {editForm.comprovante_correios_url ? 'Alterar Comprovante' : 'Anexar Comprovante'}
                    </Button>
                  </div>
                  
                  {editForm.comprovante_correios_url ? (
                    <div className="bg-white p-3 rounded border border-yellow-300 mt-3">
                      <div className="flex items-center gap-2">
                        <FileText className="text-yellow-600" size={16} />
                        <div>
                          <p className="text-sm font-medium">Comprovante anexado</p>
                          <Button
                            size="sm"
                            variant="link"
                            onClick={() => window.open(getComprovanteUrl(editForm.comprovante_correios_url), '_blank')}
                            className="text-yellow-600 px-0 h-auto"
                          >
                            Visualizar comprovante
                          </Button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-600 mt-2">Nenhum comprovante anexado</p>
                  )}
                </div>
              )}

              {/* Comprovante de Entrega */}
              {editForm.comprovante_entrega_url && (
                <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <FileText className="text-green-600" size={20} />
                      <div>
                        <p className="font-medium text-green-800">Comprovante de Entrega Anexado</p>
                        <p className="text-sm text-green-600">Clique para visualizar o documento</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => window.open(getComprovanteUrl(editForm.comprovante_entrega_url), '_blank')}
                        className="text-green-600 hover:bg-green-100 border-green-300"
                      >
                        <ExternalLink size={16} className="mr-2" />
                        Abrir
                      </Button>
                      {!isViewOnlyMode && (
                        <label className="cursor-pointer">
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-orange-600 hover:bg-orange-100 border-orange-300"
                            disabled={uploadingComprovante}
                            asChild
                          >
                            <span>
                              <Upload size={16} className="mr-2" />
                              {uploadingComprovante ? 'Enviando...' : 'Trocar'}
                            </span>
                          </Button>
                          <input
                            type="file"
                            className="hidden"
                            accept="image/jpeg,image/png,image/jpg,application/pdf"
                            onChange={handleUploadComprovanteEntrega}
                            disabled={uploadingComprovante}
                          />
                        </label>
                      )}
                    </div>
                  </div>
                  {editForm.data_entrega_real && (
                    <p className="text-sm text-gray-600 mt-2">
                      Data de entrega: {formatDateBR(editForm.data_entrega_real)}
                    </p>
                  )}
                  
                  {/* Área para colar novo print (Ctrl+V) - para trocar */}
                  {!isViewOnlyMode && (
                    <div 
                      className="mt-3 border-2 border-dashed border-orange-300 rounded-lg p-4 bg-orange-50 hover:bg-orange-100 transition-colors cursor-pointer text-center"
                      tabIndex={0}
                      onPaste={handlePasteComprovante}
                      onClick={(e) => e.currentTarget.focus()}
                    >
                      <div className="flex items-center justify-center gap-2 text-orange-600">
                        <Clipboard size={18} />
                        <span className="text-sm font-medium">Cole um novo print aqui (Ctrl+V) para trocar</span>
                      </div>
                    </div>
                  )}
                  
                  {uploadingComprovante && (
                    <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded flex items-center gap-2">
                      <Loader2 className="animate-spin h-4 w-4 text-blue-600" />
                      <span className="text-sm text-blue-700">Enviando novo comprovante...</span>
                    </div>
                  )}
                </div>
              )}

              {/* Adicionar comprovante se não existir */}
              {!editForm.comprovante_entrega_url && !isViewOnlyMode && (
                <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Upload className="text-gray-500" size={20} />
                      <div>
                        <p className="font-medium text-gray-700">Comprovante de Entrega</p>
                        <p className="text-sm text-gray-500">Nenhum comprovante anexado</p>
                      </div>
                    </div>
                    <label className="cursor-pointer">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={uploadingComprovante}
                        asChild
                      >
                        <span>
                          <Upload size={16} className="mr-2" />
                          {uploadingComprovante ? 'Enviando...' : 'Selecionar Arquivo'}
                        </span>
                      </Button>
                      <input
                        type="file"
                        className="hidden"
                        accept="image/jpeg,image/png,image/jpg,application/pdf"
                        onChange={handleUploadComprovanteEntrega}
                        disabled={uploadingComprovante}
                      />
                    </label>
                  </div>
                  
                  {/* Área para colar print (Ctrl+V) */}
                  <div 
                    className="border-2 border-dashed border-blue-300 rounded-lg p-6 bg-blue-50 hover:bg-blue-100 transition-colors cursor-pointer text-center"
                    tabIndex={0}
                    onPaste={handlePasteComprovante}
                    onClick={(e) => e.currentTarget.focus()}
                  >
                    <Clipboard className="mx-auto text-blue-500 mb-2" size={32} />
                    <p className="text-blue-700 font-medium">Cole aqui o print (Ctrl+V)</p>
                    <p className="text-sm text-blue-600 mt-1">
                      Tire um print da tela dos Correios e cole diretamente aqui
                    </p>
                    <div className="flex items-center justify-center gap-2 mt-3 text-xs text-blue-500">
                      <Image size={14} />
                      <span>Aceita imagens PNG, JPG ou PDF</span>
                    </div>
                  </div>
                  
                  {uploadingComprovante && (
                    <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded flex items-center gap-2">
                      <Loader2 className="animate-spin h-4 w-4 text-blue-600" />
                      <span className="text-sm text-blue-700">Enviando comprovante...</span>
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-end gap-3 mt-6">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowEditModal(false);
                    setIsViewOnlyMode(false);
                  }}
                  disabled={loading}
                >
                  {isViewOnlyMode ? 'Fechar' : 'Cancelar'}
                </Button>
                {!isViewOnlyMode && (
                  <Button
                    onClick={handleSaveEdit}
                    disabled={loading}
                    style={{
                      backgroundColor: colors.primaza.primary,
                      color: 'white'
                    }}
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Salvando...
                      </>
                    ) : (
                      <>
                        <Save className="mr-2 h-4 w-4" />
                        Salvar Alterações
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Histórico */}
      <ModalHistoricoPedido
        isOpen={historicoModal.isOpen}
        onClose={() => setHistoricoModal({ isOpen: false, pedido: null })}
        pedido={historicoModal.pedido}
      />

      {/* Modal de Comprovante Correios */}
      <ModalComprovanteCorreios
        isOpen={correiosModal.isOpen}
        onClose={() => setCorreiosModal({ isOpen: false, pedido: null })}
        pedido={correiosModal.pedido}
        onSuccess={() => {
          loadPedidos(paginaAtual, buscaGlobal, filtroStatus, filtroTransportadora);
          setCorreiosModal({ isOpen: false, pedido: null });
        }}
      />

      {/* Modal de Confirmação de Entrega */}
      <ModalConfirmacaoEntrega
        isOpen={modalEntrega.isOpen}
        onClose={() => setModalEntrega({ isOpen: false, pedido: null, dataPreenchida: null })}
        pedido={modalEntrega.pedido}
        dataPreenchida={modalEntrega.dataPreenchida}
        onSuccess={() => {
          loadPedidos(paginaAtual, buscaGlobal, filtroStatus, filtroTransportadora);
          setModalEntrega({ isOpen: false, pedido: null, dataPreenchida: null });
        }}
      />

      {/* Modal de Rastreamento Correios - Design Moderno */}
      {rastreamentoModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
            {/* Header com gradiente */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-5">
              <div className="flex items-start justify-between text-white">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="bg-white/20 p-2 rounded-lg">
                      <Truck size={24} />
                    </div>
                    <div>
                      <h3 className="font-bold text-xl">Rastreamento Correios</h3>
                      <p className="text-sm text-blue-100 mt-0.5">
                        Pedido #{rastreamentoModal.pedido?.pedido}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 mt-3">
                    <span className="bg-white/20 px-3 py-1 rounded-full text-xs font-medium">
                      {rastreamentoModal.pedido?.cod_rastreamento}
                    </span>
                    {rastreamentoModal.pedido?.prev_entrega && !rastreamentoModal.dados?.entregue && (
                      <span className="bg-white/20 px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                        <Calendar size={12} />
                        Previsão: {formatDateBR(rastreamentoModal.pedido.prev_entrega)}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => setRastreamentoModal({ isOpen: false, pedido: null, dados: null, loading: false })}
                  className="text-white/90 hover:text-white hover:bg-white/10 rounded-lg p-2 transition-all"
                >
                  <X size={24} />
                </button>
              </div>
            </div>
            
            {/* Conteúdo */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
              {rastreamentoModal.loading ? (
                <div className="flex flex-col items-center justify-center py-16">
                  <Loader2 className="h-16 w-16 animate-spin text-blue-500 mb-4" />
                  <p className="text-gray-600 font-medium">Buscando informações nos Correios...</p>
                  <p className="text-gray-400 text-sm mt-2">Isso pode levar alguns segundos</p>
                </div>
              ) : rastreamentoModal.dados?.erro ? (
                <div className="py-12">
                  <Alert variant="destructive" className="max-w-md mx-auto">
                    <AlertDescription className="text-center">
                      {rastreamentoModal.dados.erro}
                    </AlertDescription>
                  </Alert>
                </div>
              ) : rastreamentoModal.dados ? (
                <div className="space-y-6">
                  {/* Status Atual - Card Destacado */}
                  <div className={`rounded-xl p-5 border-2 ${rastreamentoModal.dados.entregue ? 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-200' : 'bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200'}`}>
                    <div className="flex items-center gap-4">
                      <div className={`p-3 rounded-xl ${rastreamentoModal.dados.entregue ? 'bg-green-100' : 'bg-blue-100'}`}>
                        {rastreamentoModal.dados.entregue ? (
                          <CheckCircle className={`h-8 w-8 text-green-600`} />
                        ) : (
                          <Package className={`h-8 w-8 text-blue-600`} />
                        )}
                      </div>
                      <div className="flex-1">
                        <p className={`font-bold text-lg ${rastreamentoModal.dados.entregue ? 'text-green-800' : 'text-blue-800'}`}>
                          {rastreamentoModal.dados.ultimo_status || 'Sem informações'}
                        </p>
                        <div className="flex flex-wrap items-center gap-3 mt-1 text-sm text-gray-600">
                          {rastreamentoModal.dados.ultima_data && (
                            <span className="flex items-center gap-1">
                              <Clock size={14} />
                              {formatDateBR(rastreamentoModal.dados.ultima_data)}
                            </span>
                          )}
                          {rastreamentoModal.dados.ultima_localizacao && (
                            <span className="flex items-center gap-1">
                              <MapPin size={14} />
                              {rastreamentoModal.dados.ultima_localizacao}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Card de Sugestão de Atualização - SOMENTE ADMIN/LANÇADOR */}
                  {user.role !== 'vendedor' && (() => {
                    const sugestao = devesugerirAtualizacao();
                    if (!sugestao) return null;
                    
                    return (
                      <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border-2 border-amber-200 rounded-xl p-5">
                        <div className="flex items-start gap-4">
                          <div className="flex-shrink-0 bg-amber-100 p-2 rounded-lg">
                            <svg className="h-6 w-6 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </div>
                          <div className="flex-1">
                            <h4 className="font-bold text-amber-900 mb-2 text-lg">
                              💡 Sugestão de Atualização
                            </h4>
                            <p className="text-amber-800 mb-2">
                              {sugestao.mensagem}
                            </p>
                            <p className="text-sm text-amber-700 mb-4 bg-white/50 px-3 py-2 rounded-lg">
                              Status atual no sistema: <span className="font-semibold">{rastreamentoModal.pedido?.status_pedido || 'Não definido'}</span>
                            </p>
                            <Button
                              className="bg-amber-600 hover:bg-amber-700 text-white font-medium shadow-md"
                              onClick={abrirModalConfirmacaoEntrega}
                              size="sm"
                            >
                              <CheckCircle size={16} className="mr-2" />
                              Confirmar Entrega ({sugestao.statusSugerido})
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Linha do Tempo de Eventos */}
                  {rastreamentoModal.dados.eventos && rastreamentoModal.dados.eventos.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-4">
                        <div className="bg-blue-100 p-2 rounded-lg">
                          <Clock size={18} className="text-blue-600" />
                        </div>
                        <h4 className="font-bold text-gray-800 text-lg">
                          Histórico de Movimentação
                        </h4>
                        <span className="text-sm text-gray-500 ml-auto">
                          {rastreamentoModal.dados.eventos.length} eventos
                        </span>
                      </div>
                      
                      <div className="relative">
                        {/* Linha vertical amarela */}
                        <div className="absolute left-[21px] top-4 bottom-4 w-0.5 bg-gradient-to-b from-yellow-400 via-yellow-500 to-yellow-400"></div>
                        
                        <div className="space-y-4">
                          {rastreamentoModal.dados.eventos.map((evento, index) => (
                            <div 
                              key={index} 
                              className="relative pl-14"
                            >
                              {/* Ícone do evento */}
                              <div className={`absolute left-0 top-0 p-2.5 rounded-xl ${
                                index === 0 
                                  ? 'bg-blue-100 ring-4 ring-blue-50' 
                                  : 'bg-yellow-50 ring-2 ring-yellow-100'
                              }`}>
                                {index === 0 ? (
                                  <Package size={18} className="text-blue-600" />
                                ) : evento.descricao.toLowerCase().includes('entrega') ? (
                                  <Truck size={18} className="text-yellow-600" />
                                ) : (
                                  <MapPin size={18} className="text-yellow-600" />
                                )}
                              </div>
                              
                              {/* Conteúdo do evento */}
                              <div className={`rounded-xl p-4 ${
                                index === 0 
                                  ? 'bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 shadow-sm' 
                                  : 'bg-white border border-gray-200 hover:border-gray-300 transition-colors'
                              }`}>
                                <p className={`font-semibold mb-2 ${
                                  index === 0 ? 'text-blue-900' : 'text-gray-800'
                                }`}>
                                  {evento.descricao}
                                </p>
                                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-600">
                                  {evento.data && (
                                    <span className="flex items-center gap-1.5 bg-gray-50 px-2 py-1 rounded-md">
                                      <Clock size={14} className="text-gray-500" />
                                      <span className="font-medium">{formatDateBR(evento.data)}</span>
                                    </span>
                                  )}
                                  {evento.localizacao && (
                                    <span className="flex items-center gap-1.5 bg-gray-50 px-2 py-1 rounded-md">
                                      <MapPin size={14} className="text-gray-500" />
                                      <span className="font-medium">{evento.localizacao}</span>
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Rodapé com informações */}
                  <div className="flex items-center justify-center gap-2 pt-4 text-xs text-gray-400 border-t">
                    <span className="bg-yellow-100 text-yellow-700 px-2 py-1 rounded font-medium">
                      Fonte: Correios API Oficial
                    </span>
                  </div>
                </div>
              ) : null}
            </div>

            {/* Footer com botões */}
            <div className="border-t bg-gray-50 px-6 py-4 flex justify-between items-center">
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => buscarRastreamento(rastreamentoModal.pedido)}
                  disabled={rastreamentoModal.loading}
                  className="font-medium"
                >
                  <RefreshCw size={16} className={`mr-2 ${rastreamentoModal.loading ? 'animate-spin' : ''}`} />
                  Atualizar
                </Button>
                <Button
                  variant="outline"
                  onClick={copiarLinkPublico}
                  className={`font-medium transition-all ${linkCopiado ? 'bg-green-50 border-green-300 text-green-700' : ''}`}
                >
                  {linkCopiado ? (
                    <>
                      <CheckCircle size={16} className="mr-2" />
                      Link Copiado!
                    </>
                  ) : (
                    <>
                      <Clipboard size={16} className="mr-2" />
                      Copiar Link
                    </>
                  )}
                </Button>
                <Button
                  onClick={compartilharWhatsApp}
                  className="bg-green-600 hover:bg-green-700 text-white font-medium"
                >
                  <svg className="mr-2 h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                  </svg>
                  WhatsApp
                </Button>
              </div>
              <Button
                onClick={() => setRastreamentoModal({ isOpen: false, pedido: null, dados: null, loading: false })}
                className="bg-blue-600 hover:bg-blue-700 text-white font-medium"
              >
                Fechar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal API Transportadora (São Miguel / Braspress) */}
      {ediModal.isOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl overflow-hidden">
            {/* Header dinâmico por transportadora */}
            {(() => {
              const isBP = isBraspress(ediModal.pedido?.transportadora);
              const isRN = isRodonavez(ediModal.pedido?.transportadora);
              const carrierName = isBP ? 'Braspress' : isRN ? 'Rodonaves' : 'São Miguel';
              const gradientClass = isBP ? 'bg-gradient-to-r from-orange-500 to-orange-700' : isRN ? 'bg-gradient-to-r from-blue-500 to-blue-700' : 'bg-gradient-to-r from-purple-600 to-purple-800';
              const subColor = isBP ? 'text-orange-100' : isRN ? 'text-blue-100' : 'text-purple-100';
              return (
            <div className={`${gradientClass} text-white p-6`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="bg-white/20 p-3 rounded-xl">
                    <Truck size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold flex items-center gap-2">
                      <span>API {carrierName}</span>
                      <span className="text-sm font-normal opacity-80">
                        Pedido #{ediModal.pedido?.pedido}
                      </span>
                    </h3>
                    <p className={`${subColor} text-sm mt-1`}>
                      NF: {ediModal.pedido?.nfe}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setEdiModal({ isOpen: false, pedido: null, dados: null, loading: false })}
                  className="text-white/80 hover:text-white transition-colors"
                >
                  <X size={24} />
                </button>
              </div>
            </div>
              );
            })()}

            {/* Conteúdo */}
            <div className="p-6 max-h-[60vh] overflow-y-auto">
              {ediModal.loading ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <Loader2 className={`h-12 w-12 animate-spin ${isBraspress(ediModal.pedido?.transportadora) ? 'text-orange-600' : isRodonavez(ediModal.pedido?.transportadora) ? 'text-blue-600' : 'text-purple-600'}`} />
                  <p className="mt-4 text-gray-600">Consultando API {isBraspress(ediModal.pedido?.transportadora) ? 'Braspress' : isRodonavez(ediModal.pedido?.transportadora) ? 'Rodonaves' : 'São Miguel'}...</p>
                </div>
              ) : ediModal.dados?.erro ? (
                <div className="text-center py-8">
                  <div className="bg-yellow-50 rounded-xl p-6 border border-yellow-200">
                    <p className="text-yellow-800">{ediModal.dados.erro}</p>
                    <p className="text-sm text-yellow-600 mt-2">
                      Este pedido ainda não recebeu atualizações via API
                    </p>
                  </div>
                </div>
              ) : ediModal.dados ? (
                <div className="space-y-6">
                  {/* Status atual */}
                  {(() => {
                    const statusMap = {
                      'ENTREGUE': { label: 'Entregue', color: 'text-green-800', bg: 'from-green-50 to-emerald-50', border: 'border-green-200', iconBg: 'bg-green-100', iconColor: 'text-green-600' },
                      'SAIU_PARA_ENTREGA': { label: 'Saiu para Entrega', color: 'text-purple-800', bg: 'from-purple-50 to-indigo-50', border: 'border-purple-200', iconBg: 'bg-purple-100', iconColor: 'text-purple-600' },
                      'NA_UNIDADE_DESTINO': { label: 'Na Filial Destino', color: 'text-cyan-800', bg: 'from-cyan-50 to-teal-50', border: 'border-cyan-200', iconBg: 'bg-cyan-100', iconColor: 'text-cyan-600' },
                      'EM_TRANSITO': { label: 'Em Transito', color: 'text-blue-800', bg: 'from-blue-50 to-indigo-50', border: 'border-blue-200', iconBg: 'bg-blue-100', iconColor: 'text-blue-600' },
                      'COLETADO': { label: 'Coletado', color: 'text-yellow-800', bg: 'from-yellow-50 to-amber-50', border: 'border-yellow-200', iconBg: 'bg-yellow-100', iconColor: 'text-yellow-600' },
                      'FATURADO': { label: 'Faturado', color: 'text-gray-800', bg: 'from-gray-50 to-slate-50', border: 'border-gray-200', iconBg: 'bg-gray-100', iconColor: 'text-gray-600' },
                      'DEVOLVIDO': { label: 'Devolvido', color: 'text-red-800', bg: 'from-red-50 to-rose-50', border: 'border-red-200', iconBg: 'bg-red-100', iconColor: 'text-red-600' },
                    };
                    const st = statusMap[ediModal.dados.status_pedido] || statusMap['EM_TRANSITO'];
                    const statusLabel = ediModal.dados.edi_descricao || ediModal.dados.ultima_ocorrencia_braspress || st.label;
                    return (
                  <div className={`rounded-xl p-5 border-2 bg-gradient-to-br ${st.bg} ${st.border}`}>
                    <div className="flex items-center gap-4">
                      <div className={`p-3 rounded-xl ${st.iconBg}`}>
                        {ediModal.dados.status_pedido === 'ENTREGUE' ? (
                          <CheckCircle size={28} className={st.iconColor} />
                        ) : ediModal.dados.status_pedido === 'SAIU_PARA_ENTREGA' ? (
                          <Truck size={28} className={st.iconColor} />
                        ) : ediModal.dados.status_pedido === 'NA_UNIDADE_DESTINO' ? (
                          <MapPin size={28} className={st.iconColor} />
                        ) : ediModal.dados.status_pedido === 'COLETADO' ? (
                          <Package size={28} className={st.iconColor} />
                        ) : (
                          <Truck size={28} className={st.iconColor} />
                        )}
                      </div>
                      <div className="flex-1">
                        <p className={`font-bold text-lg ${st.color}`}>
                          {statusLabel}
                        </p>
                        <div className="flex flex-wrap gap-3 mt-1 text-sm text-gray-600">
                          {ediModal.dados.edi_data_ocorrencia && (
                            <span className="flex items-center gap-1">
                              <Calendar size={14} />
                              {ediModal.dados.edi_data_ocorrencia.split('-').reverse().join('/')}
                            </span>
                          )}
                          {ediModal.dados.edi_hora_ocorrencia && (
                            <span className="flex items-center gap-1">
                              <Clock size={14} />
                              {ediModal.dados.edi_hora_ocorrencia}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                    );

                  {/* Informações do cliente */}
                  })()}
                  <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Cliente:</span>
                      <span className="font-medium">{ediModal.dados.nome_cliente}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Transportadora:</span>
                      <span className="font-medium">{ediModal.dados.transportadora}</span>
                    </div>
                    {ediModal.dados.edi_arquivo_origem && !ediModal.dados.api_tracking_eventos?.length && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Arquivo EDI:</span>
                        <span className="font-mono text-xs">{ediModal.dados.edi_arquivo_origem}</span>
                      </div>
                    )}
                  </div>

                  {/* Dados da Transportadora (preenchidos automaticamente pela API) */}
                  {ediModal.dados.cte_transportadora && (
                    <div className="border border-teal-200 rounded-xl overflow-hidden">
                      <div className="bg-teal-50 px-4 py-3 border-b border-teal-100">
                        <h4 className="font-semibold text-teal-900 flex items-center gap-2">
                          <FileText size={16} />
                          Dados da Transportadora
                        </h4>
                      </div>
                      <div className="p-4 grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <span className="text-gray-500">CT-e:</span>
                          <span className="ml-2 font-medium">{ediModal.dados.cte_transportadora}</span>
                        </div>
                        {ediModal.dados.frete_transportadora != null && (
                          <div>
                            <span className="text-gray-500">Frete:</span>
                            <span className="ml-2 font-medium text-green-700">
                              R$ {Number(ediModal.dados.frete_transportadora).toFixed(2)}
                            </span>
                          </div>
                        )}
                        {ediModal.dados.valor_mercadoria_transportadora != null && (
                          <div>
                            <span className="text-gray-500">Valor Mercadoria:</span>
                            <span className="ml-2 font-medium">
                              R$ {Number(ediModal.dados.valor_mercadoria_transportadora).toFixed(2)}
                            </span>
                          </div>
                        )}
                        {ediModal.dados.peso_m3_transportadora != null && (
                          <div>
                            <span className="text-gray-500">Peso/M3:</span>
                            <span className="ml-2 font-medium">{ediModal.dados.peso_m3_transportadora}</span>
                          </div>
                        )}
                        {ediModal.dados.origem_transportadora && (
                          <div>
                            <span className="text-gray-500">Origem:</span>
                            <span className="ml-2 font-medium">{ediModal.dados.origem_transportadora}</span>
                          </div>
                        )}
                        {ediModal.dados.destino_transportadora && (
                          <div>
                            <span className="text-gray-500">Destino:</span>
                            <span className="ml-2 font-medium">{ediModal.dados.destino_transportadora}</span>
                          </div>
                        )}
                        {ediModal.dados.unidade_destino_transportadora && (
                          <div className="col-span-2">
                            <span className="text-gray-500">Unidade Destino:</span>
                            <span className="ml-2 font-medium">{ediModal.dados.unidade_destino_transportadora}</span>
                          </div>
                        )}
                        {ediModal.dados.endereco_entrega_transportadora && (
                          <div className="col-span-2">
                            <span className="text-gray-500">Endereço Entrega:</span>
                            <span className="ml-2 font-medium text-xs">{ediModal.dados.endereco_entrega_transportadora}</span>
                          </div>
                        )}
                        {ediModal.dados.data_entrega_transportadora && (
                          <div>
                            <span className="text-gray-500">Data Entrega:</span>
                            <span className="ml-2 font-medium text-green-700">
                              {(() => {
                                const d = ediModal.dados.data_entrega_transportadora;
                                if (!d) return '-';
                                const dateStr = String(d);
                                const parsed = new Date(dateStr);
                                if (!isNaN(parsed.getTime())) return parsed.toLocaleDateString('pt-BR');
                                if (dateStr.includes('/')) return dateStr.split(' ')[0];
                                if (dateStr.includes('-')) {
                                  const parts = dateStr.split('T')[0].split('-');
                                  if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
                                }
                                return dateStr;
                              })()}
                            </span>
                          </div>
                        )}
                        {ediModal.dados.tipo_frete && (
                          <div>
                            <span className="text-gray-500">Tipo Frete:</span>
                            <span className={`ml-2 font-bold ${ediModal.dados.tipo_frete === 'CIF' ? 'text-blue-700' : 'text-orange-700'}`}>
                              {ediModal.dados.tipo_frete}
                            </span>
                          </div>
                        )}
                        {ediModal.dados.peso_transportadora != null && (
                          <div>
                            <span className="text-gray-500">Peso:</span>
                            <span className="ml-2 font-medium">{ediModal.dados.peso_transportadora} kg</span>
                          </div>
                        )}
                        {ediModal.dados.volumes_transportadora != null && (
                          <div>
                            <span className="text-gray-500">Volumes:</span>
                            <span className="ml-2 font-medium">{ediModal.dados.volumes_transportadora}</span>
                          </div>
                        )}
                        {ediModal.dados.remetente_transportadora && (
                          <div className="col-span-2">
                            <span className="text-gray-500">Remetente:</span>
                            <span className="ml-2 font-medium text-xs">{ediModal.dados.remetente_transportadora}</span>
                          </div>
                        )}
                        {ediModal.dados.destinatario_transportadora && (
                          <div className="col-span-2">
                            <span className="text-gray-500">Destinatario:</span>
                            <span className="ml-2 font-medium text-xs">{ediModal.dados.destinatario_transportadora}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Previsão de entrega (via API) */}
                  {(ediModal.dados.previsao_entrega_oficial || ediModal.dados.previsao_entrega_transportadora) && (
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-blue-700 font-medium flex items-center gap-2">
                          <Calendar size={16} />
                          Previsao de Entrega
                        </span>
                        <span className="text-lg font-bold text-blue-800">
                          {(() => {
                            const d = ediModal.dados.previsao_entrega_oficial || ediModal.dados.previsao_entrega_transportadora || '';
                            const s = String(d);
                            if (s.includes('/')) return s.split(' ')[0];
                            if (s.includes('-')) {
                              const parts = s.split('T')[0].split('-');
                              if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
                            }
                            try { return new Date(s).toLocaleDateString('pt-BR'); } catch { return s; }
                          })()}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Histórico de eventos API Tracking */}
                  {ediModal.dados.api_tracking_eventos && ediModal.dados.api_tracking_eventos.length > 0 && (
                    <div className="border rounded-xl overflow-hidden">
                      <div className="bg-teal-50 px-4 py-3 border-b border-teal-100">
                        <h4 className="font-semibold text-teal-900 flex items-center gap-2">
                          <Activity size={18} />
                          Tracking API
                          <span className="bg-teal-200 text-teal-800 px-2 py-0.5 rounded-full text-xs">
                            {ediModal.dados.api_tracking_eventos.length} eventos
                          </span>
                        </h4>
                      </div>
                      <div className="divide-y">
                        {ediModal.dados.api_tracking_eventos.slice().reverse().map((evento, index, arr) => {
                          const statusMap = {
                            'ENTREGUE': { label: 'Entregue', color: 'text-green-700', bg: 'bg-green-100', icon: 'check' },
                            'SAIU_PARA_ENTREGA': { label: 'Saiu p/ Entrega', color: 'text-purple-700', bg: 'bg-purple-100', icon: 'truck' },
                            'NA_UNIDADE_DESTINO': { label: 'Na Filial Destino', color: 'text-cyan-700', bg: 'bg-cyan-100', icon: 'mappin' },
                            'EM_TRANSITO': { label: 'Em Transito', color: 'text-blue-700', bg: 'bg-blue-100', icon: 'truck' },
                            'COLETADO': { label: 'Coletado', color: 'text-yellow-700', bg: 'bg-yellow-100', icon: 'package' },
                            'DEVOLVIDO': { label: 'Devolvido', color: 'text-red-700', bg: 'bg-red-100', icon: 'x' },
                          };
                          const st = statusMap[evento.status_normalizado] || { label: evento.status_normalizado, color: 'text-gray-700', bg: 'bg-gray-100', icon: 'truck' };
                          
                          // Parse date from either format
                          const rawDate = evento.dataRegistro || evento.data || '';
                          let dateStr = '';
                          let timeStr = '';
                          if (rawDate) {
                            const s = String(rawDate);
                            if (s.includes('T')) {
                              // ISO format
                              const d = new Date(s);
                              if (!isNaN(d.getTime())) {
                                dateStr = d.toLocaleDateString('pt-BR');
                                timeStr = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
                              }
                            } else if (s.includes('/')) {
                              // dd/mm/yyyy HH:mm format
                              const parts = s.split(' ');
                              dateStr = parts[0] || '';
                              timeStr = parts[1] || '';
                            } else {
                              dateStr = s;
                            }
                          }
                          
                          return (
                          <div 
                            key={index} 
                            className={`p-4 flex items-start gap-4 ${index === 0 ? 'bg-teal-50/50' : ''}`}
                          >
                            <div className="flex flex-col items-center">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${st.bg}`}>
                                {st.icon === 'check' ? (
                                  <CheckCircle size={20} className="text-green-600" />
                                ) : st.icon === 'package' ? (
                                  <Package size={20} className="text-yellow-600" />
                                ) : st.icon === 'mappin' ? (
                                  <MapPin size={20} className="text-cyan-600" />
                                ) : st.icon === 'x' ? (
                                  <XCircle size={20} className="text-red-600" />
                                ) : evento.status_normalizado === 'SAIU_PARA_ENTREGA' ? (
                                  <Truck size={20} className="text-purple-600" />
                                ) : (
                                  <Truck size={20} className="text-blue-600" />
                                )}
                              </div>
                              {index < arr.length - 1 && (
                                <div className="w-0.5 h-full bg-gray-200 mt-2"></div>
                              )}
                            </div>
                            <div className="flex-1">
                              <p className={`font-semibold ${st.color}`}>
                                {evento.descricao}
                              </p>
                              <div className="flex flex-wrap gap-3 mt-1 text-sm text-gray-500">
                                {dateStr && (
                                  <span className="flex items-center gap-1">
                                    <Calendar size={12} />
                                    {dateStr}
                                  </span>
                                )}
                                {timeStr && (
                                  <span className="flex items-center gap-1">
                                    <Clock size={12} />
                                    {timeStr}
                                  </span>
                                )}
                              </div>
                              <span className={`inline-block mt-1 text-xs px-2 py-0.5 rounded-full ${st.bg} ${st.color}`}>
                                {st.label}
                              </span>
                            </div>
                          </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Comprovante de Entrega */}
                  {ediModal.dados.comprovante_entrega && (
                    <div className="border border-green-200 rounded-xl overflow-hidden">
                      <div className="bg-green-50 px-4 py-3 border-b border-green-100">
                        <h4 className="font-semibold text-green-900 flex items-center gap-2">
                          <CheckCircle size={18} />
                          Comprovante de Entrega
                        </h4>
                      </div>
                      <div className="p-4 space-y-2">
                        {ediModal.dados.comprovante_entrega.latitude && (
                          <p className="text-sm text-gray-600">
                            Localização: {ediModal.dados.comprovante_entrega.latitude}, {ediModal.dados.comprovante_entrega.longitude}
                          </p>
                        )}
                        {ediModal.dados.comprovante_entrega.qtd_fotos > 0 && (
                          <p className="text-sm text-gray-600">
                            {ediModal.dados.comprovante_entrega.qtd_fotos} foto(s) de comprovação
                          </p>
                        )}
                        {ediModal.dados.comprovante_entrega.fotos_base64?.length > 0 && (
                          <div className="flex gap-2 flex-wrap">
                            {ediModal.dados.comprovante_entrega.fotos_base64
                              .filter((foto, i, arr) => arr.findIndex(f => f.substring(0, 100) === foto.substring(0, 100)) === i)
                              .map((foto, i) => (
                              <img
                                key={i}
                                src={foto.startsWith('data:') ? foto : `data:image/jpeg;base64,${foto}`}
                                alt={`Comprovante ${i + 1}`}
                                className="h-32 rounded-lg border shadow-sm cursor-pointer hover:opacity-90"
                                onClick={() => window.open(foto.startsWith('data:') ? foto : `data:image/jpeg;base64,${foto}`, '_blank')}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Sem histórico */}
                  {(!ediModal.dados.api_tracking_eventos || ediModal.dados.api_tracking_eventos.length === 0) && (
                    <div className="bg-gray-50 rounded-xl p-6 text-center">
                      <p className="text-gray-500">Nenhum evento de rastreamento registrado ainda</p>
                    </div>
                  )}
                </div>
              ) : null}
            </div>

            {/* Footer com botões de compartilhar */}
            <div className="border-t bg-gray-50 px-6 py-4">
              {/* Botões de compartilhar */}
              {ediModal.dados && ediModal.pedido?.nfe && (
                <div className="flex gap-2 mb-3">
                  <Button
                    onClick={compartilharEdiWhatsApp}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white font-medium"
                  >
                    <svg viewBox="0 0 24 24" className="w-4 h-4 mr-2 fill-current">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                    </svg>
                    WhatsApp
                  </Button>
                  <Button
                    onClick={copiarLinkEdi}
                    variant="outline"
                    className="flex-1 font-medium border-purple-300 text-purple-700 hover:bg-purple-50"
                  >
                    {linkCopiado ? (
                      <>
                        <CheckCircle size={16} className="mr-2 text-green-600" />
                        Copiado!
                      </>
                    ) : (
                      <>
                        <Clipboard size={16} className="mr-2" />
                        Copiar Link
                      </>
                    )}
                  </Button>
                </div>
              )}
              
              <div className="flex justify-between items-center">
                <Button
                  variant="outline"
                  onClick={() => buscarDadosEDI(ediModal.pedido)}
                  disabled={ediModal.loading}
                  className={`font-medium ${isBraspress(ediModal.pedido?.transportadora) ? 'border-orange-300 text-orange-700 hover:bg-orange-50' : isRodonavez(ediModal.pedido?.transportadora) ? 'border-blue-300 text-blue-700 hover:bg-blue-50' : 'border-purple-300 text-purple-700 hover:bg-purple-50'}`}
                >
                  <RefreshCw size={16} className={`mr-2 ${ediModal.loading ? 'animate-spin' : ''}`} />
                  Atualizar
                </Button>
                <Button
                  onClick={() => setEdiModal({ isOpen: false, pedido: null, dados: null, loading: false })}
                  className={`${isBraspress(ediModal.pedido?.transportadora) ? 'bg-orange-600 hover:bg-orange-700' : isRodonavez(ediModal.pedido?.transportadora) ? 'bg-blue-600 hover:bg-blue-700' : 'bg-purple-600 hover:bg-purple-700'} text-white font-medium`}
                >
                  Fechar
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConsultarPedidos;
